import { useState, useEffect, useRef } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import { MapPin, Phone, ExternalLink, Calendar, Building2, Shield, Users, Banknote, Globe, Car, Calculator, BookOpen, Heart, Share2, ArrowRightLeft, ChevronLeft, ChevronRight, X, MessageSquare, Clock, Stethoscope, BedDouble, Siren, AlertTriangle } from 'lucide-react'
import { motion, AnimatePresence } from 'framer-motion'
import { API_BASE_URL } from '@/lib/constants'
import Header from '@/components/layout/Header'
import PageContainer from '@/components/layout/PageContainer'
import Card from '@/components/ui/Card'
import Badge from '@/components/ui/Badge'
import Button from '@/components/ui/Button'
import Skeleton from '@/components/ui/Skeleton'
import ProgressBar from '@/components/common/ProgressBar'
import TabGroup, { TabPanel } from '@/components/common/TabGroup'
import EvaluationChart from '@/components/facility/EvaluationChart'
import FacilityReviewTab from '@/components/facility/FacilityReviewTab'
import EmptyState from '@/components/common/EmptyState'
import { useFacility, useToggleBookmark, useBookmarkIds, useNearbyFacilities } from '@/features/facility/api'
import type { FacilityDetail, HospitalDetail, TreatmentTimeSlot } from '@/types/facility'
import PhotoGallery from '@/components/facility/PhotoGallery'
import { useQuery as useQ } from '@tanstack/react-query'
import apiClient from '@/lib/api/client'
import ConsultationModal from '@/components/consultation/ConsultationModal'
import ChecklistPromoCard from '@/components/checklist/ChecklistPromoCard'
import { toast } from '@/components/ui/Toast'
import { useAuthStore } from '@/stores/authStore'
import { useHasMounted } from '@/hooks/useHasMounted'
import type { EvaluationGrade, FacilityType } from '@/types/common'
import { FACILITY_TYPE_LABELS, getFacilityTypeUrlSlug } from '@/types/common'
import {
  buildFacilityTitle,
  buildFacilityDescription,
} from '@/utils/seo'
import SEO from '@/components/common/SEO'
import { FacilityJsonLd, BreadcrumbJsonLd, FAQPageJsonLd } from '@/components/common/JsonLd'
import { trackViewFacilityDetail, trackCallFacility } from '@/lib/analytics'
import { copyToClipboard } from '@/lib/clipboard'
import { formatRegionPath } from '@/lib/regions'
import { buildEvaluationSourceLabel, buildEvaluationSourcePhrase } from '@/lib/facility/evaluationLabel'
import { useSmartBack } from '@/lib/hooks/useSmartBack'
import PromotionSlot from '@/components/common/PromotionSlot'
import StickySideBanner from '@/components/common/StickySideBanner'
import { logFacilityVisitEvent } from '@/features/facility-manager/statsApi'
import { ApiError } from '@/lib/api/types'
import NotFoundPage from '@/pages/NotFoundPage'

/** 비용 계산 지원 시설 유형 */
const COST_SUPPORTED_TYPES = new Set([
  'NURSING_HOME', 'NURSING_HOME_SMALL', 'DAY_CARE',
  'HOME_VISIT_CARE', 'DEMENTIA_CARE', 'SHORT_STAY',
])

/** 탭 정의 (사진 탭은 승인된 사진이 있으면 무료 시설도 노출 — 프리미엄 차이는 업로드 한도) */
function getTabs(hasPhotos: boolean) {
  const tabs = [
    { key: 'info', label: '기본정보' },
  ]
  if (hasPhotos) {
    tabs.push({ key: 'photos', label: '사진' })
  }
  tabs.push(
    { key: 'cost', label: '비용' },
    { key: 'review', label: '후기' },
    { key: 'evaluation', label: '평가' },
    { key: 'location', label: '위치' },
  )
  return tabs
}

/** 스켈레톤 로딩 화면 */
function DetailSkeleton() {
  return (
    <div className="flex flex-col gap-5">
      <div>
        <div className="flex items-center gap-2">
          <Skeleton width="10rem" height="1.75rem" />
          <Skeleton width="3rem" height="1.5rem" />
          <Skeleton width="3rem" height="1.5rem" />
        </div>
        <div className="mt-2">
          <Skeleton width="14rem" height="1rem" />
        </div>
      </div>
      <Skeleton height="2.75rem" />
      <Card>
        <Skeleton height="1rem" />
        <div className="mt-3">
          <Skeleton height="1rem" />
        </div>
        <div className="mt-3">
          <Skeleton height="1rem" />
        </div>
        <div className="mt-3">
          <Skeleton height="1rem" />
        </div>
      </Card>
    </div>
  )
}

/**
 * 시설 상세 페이지 v2.0
 *
 * - 상단: 시설명 + 배지 더 크고 명확
 * - 탭: 더 두꺼운 스타일 + 전환 애니메이션
 * - 평가 차트: 등급별 바 색상 + 배경 그라데이션
 * - 정보 카드: 아이콘 + 라벨/값 레이아웃 개선
 */
export default function FacilityDetailPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const smartBack = useSmartBack()
  const facilityId = Number(id) || 0
  const [activeTab, setActiveTab] = useState<string>('info')
  const [isConsultationOpen, setIsConsultationOpen] = useState(false)
  const locationMapRef = useRef<HTMLDivElement>(null)
  const locationMapInstance = useRef<any>(null)

  const { data: facility, isLoading, isError, error } = useFacility(facilityId)

  useEffect(() => {
    if (facility) {
      trackViewFacilityDetail(facility.id, facility.facilityType)
      // 프리미엄 방문 통계용 PAGE_VIEW 이벤트 (비동기, 실패해도 무시)
      // TODO(Phase C 백엔드): POST /api/v1/facilities/{id}/visit-event 구현 후 활성화됨.
      //   백엔드 미구현 상태에서는 404/405로 조용히 실패한다.
      logFacilityVisitEvent(facility.id, 'PAGE_VIEW')
    }
  }, [facility])

  // 위치 탭 활성화 시 네이버 지도 초기화
  // SDK는 index.html에서 비동기 로드되므로 준비될 때까지 폴링한다.
  useEffect(() => {
    if (activeTab !== 'location') return
    if (!facility?.latitude || !facility?.longitude) return

    let disposed = false
    let pollTimer: ReturnType<typeof setInterval> | null = null

    const initMap = () => {
      if (disposed || !locationMapRef.current || !window.naver?.maps) return
      if (locationMapInstance.current) return

      const lat = Number(facility.latitude)
      const lng = Number(facility.longitude)
      const position = new window.naver.maps.LatLng(lat, lng)

      const map = new window.naver.maps.Map(locationMapRef.current, {
        center: position,
        zoom: 16,
        zoomControl: true,
        zoomControlOptions: {
          position: window.naver.maps.Position.TOP_RIGHT,
          style: window.naver.maps.ZoomControlStyle.SMALL,
        },
      })
      locationMapInstance.current = map

      new window.naver.maps.Marker({
        position,
        map,
        icon: {
          content: `<div style="
            background:#2563EB;color:white;padding:4px 10px;border-radius:16px;
            font-size:12px;font-weight:700;white-space:nowrap;
            box-shadow:0 3px 8px rgba(37,99,235,0.4);border:2.5px solid white;
          ">${facility.name}</div>`,
          anchor: new window.naver.maps.Point(50, 16),
        },
        zIndex: 200,
      })
    }

    if (window.naver?.maps) {
      initMap()
    } else {
      pollTimer = setInterval(() => {
        if (window.naver?.maps) {
          if (pollTimer) clearInterval(pollTimer)
          pollTimer = null
          initMap()
        }
      }, 100)
    }

    return () => {
      disposed = true
      if (pollTimer) clearInterval(pollTimer)
      locationMapInstance.current = null
    }
  }, [activeTab, facility?.latitude, facility?.longitude, facility?.name])

  /**
   * 전화 연결 버튼 클릭 핸들러 — PHONE_CLICK 이벤트 로깅 후 tel: 링크 호출
   * (로깅 실패해도 전화 연결은 정상 진행)
   */
  const handlePhoneClick = () => {
    if (!facility?.phone) return
    logFacilityVisitEvent(facility.id, 'PHONE_CLICK')
    window.open(`tel:${facility.phone}`)
  }

  const { data: facilityPhotos } = useQ({
    queryKey: ['facility-photos', facilityId],
    queryFn: async () => {
      const res = await apiClient.get(`/facilities/${facilityId}/photos`)
      return res.data.data ?? []
    },
    enabled: facilityId > 0,
  })
  const { data: blogPosts } = useQ<{ title: string; link: string; description: string; bloggerName: string; postDate: string }[]>({
    queryKey: ['facility-blog', facilityId],
    queryFn: async () => {
      const res = await apiClient.get(`/facilities/${facilityId}/blog-posts`)
      return res.data.data ?? []
    },
    enabled: facilityId > 0,
    staleTime: 1000 * 60 * 60,
  })
  const { toggle: toggleBookmark } = useToggleBookmark()
  const hasMounted = useHasMounted()
  const isAuthenticatedRaw = useAuthStore((s) => s.isAuthenticated)
  const isAuthenticated = hasMounted && isAuthenticatedRaw
  // 북마크 여부는 서버 상태(useBookmarkIds)에서 derived. mutation 후 invalidateQueries로
  // 자동 갱신되므로 single source of truth 보장. 이전엔 useState(false)로 초기화만 하고
  // 동기화하지 않아 이미 북마크한 시설에 add 요청 → BOOKMARK_ALREADY_EXISTS (409) 발생.
  const { data: bookmarkIds } = useBookmarkIds()
  const isBookmarked = bookmarkIds?.has(facilityId) ?? false

  /** 네이버 지도 길찾기 */
  const handleNaverDirections = () => {
    if (!facility) return
    if (facility.latitude && facility.longitude) {
      const name = encodeURIComponent(facility.name)
      window.open(
        `https://map.naver.com/p/directions/-/${facility.longitude},${facility.latitude},${name},,PLACE_POI/-/car`,
        '_blank'
      )
    } else {
      window.open(`https://map.naver.com/v5/search/${encodeURIComponent(facility.address)}`, '_blank')
    }
  }

  /** 카카오맵 길찾기 */
  const handleKakaoDirections = () => {
    if (!facility) return
    if (facility.latitude && facility.longitude) {
      window.open(
        `https://map.kakao.com/link/to/${encodeURIComponent(facility.name)},${facility.latitude},${facility.longitude}`,
        '_blank'
      )
    } else {
      window.open(`https://map.kakao.com/link/search/${encodeURIComponent(facility.address)}`, '_blank')
    }
  }

  // 유효하지 않은 ID
  if (facilityId <= 0) {
    return (
      <>
        <Header title="시설 상세" showBack />
        <PageContainer>
          <EmptyState
            message="시설 정보를 찾을 수 없어요"
            actionLabel="시설 검색으로 이동"
            onAction={() => navigate('/facilities')}
          />
        </PageContainer>
      </>
    )
  }

  // 시설 미존재 확정 (soft-404 방지) —
  //  - SSR: 백엔드가 실제 404 를 주면 프리페치가 ['facility', id] 에 null 을 시드
  //  - CSR: axios(ApiError) 의 status 404
  // 5xx/네트워크 실패/타임아웃은 아래 isError 일반 에러 UI 로 처리 (404 오폭 금지).
  const isNotFound =
    facility === null || (isError && error instanceof ApiError && error.status === 404)
  if (isNotFound) {
    return (
      <NotFoundPage
        title="시설을 찾을 수 없습니다"
        description="요청하신 시설이 존재하지 않거나 삭제되었습니다."
      />
    )
  }

  const seoInput = facility
    ? {
        name: facility.name,
        facilityTypeLabel: FACILITY_TYPE_LABELS[facility.facilityType] || '요양시설',
        addressSido: facility.addressSido,
        addressSigungu: facility.addressSigungu,
        addressDong: facility.addressDong,
        capacity: facility.capacity,
        currentCount: facility.currentCount,
        evaluationGrade: facility.evaluationGrade as string | null,
        hasDementiaCare: facility.hasDementiaCare,
      }
    : null

  // 비활성(폐업·운영정보 미확인) 시설 — 방문자 안내를 위해 200 은 유지하되 색인 제외,
  // MedicalOrganization·FAQPage JSON-LD 미출력, 상단 안내 문구 (ssr-spec §15.3, structured-data-spec §5.1).
  // sync_excluded 시설은 백엔드가 404 를 주므로 여기까지 오지 않는다.
  const isInactive = facility?.isActive === false

  return (
    <>
      {/* 좌우 세로배너 160x600 — 콘텐츠 max-w-6xl 이라 2xl 초광폭에서만, 푸터 위에서 멈춤 */}
      <StickySideBanner slotKey="facility_detail_sticky_left" side="left" topPx={340} />
      <StickySideBanner slotKey="facility_detail_sticky_right" side="right" topPx={340} />
      {facility && seoInput && (
        <>
          <SEO
            title={buildFacilityTitle(seoInput)}
            description={buildFacilityDescription(seoInput)}
            url={`/facilities/${facility.id}`}
            noindex={isInactive}
          />
          {!isInactive && (
            <FacilityJsonLd
              name={facility.name}
              address={facility.address}
              addressSido={facility.addressSido}
              addressSigungu={facility.addressSigungu}
              phone={facility.phone}
              latitude={facility.latitude}
              longitude={facility.longitude}
              evaluationGrade={facility.evaluationGrade as string}
              capacity={facility.capacity}
              currentCount={facility.currentCount}
              facilityType={facility.facilityType as string}
              hasDementiaCare={facility.hasDementiaCare}
              url={`/facilities/${facility.id}`}
            />
          )}
          <BreadcrumbJsonLd items={[
            { name: '홈', url: '/' },
            ...(facility.addressSido ? [{ name: facility.addressSido, url: `/facilities/${facility.addressSido}` }] : []),
            ...(facility.addressSigungu ? [{ name: facility.addressSigungu, url: `/facilities/${facility.addressSido}/${facility.addressSigungu}` }] : []),
            // 유형 단계는 정식 슬러그(라우트/사이트맵과 동일)가 있을 때만. 표시 라벨과 URL 슬러그가 다름에 유의.
            ...(facility.addressSido && facility.addressSigungu && getFacilityTypeUrlSlug(facility.facilityType)
              ? [{ name: FACILITY_TYPE_LABELS[facility.facilityType] || '요양시설', url: `/facilities/${facility.addressSido}/${facility.addressSigungu}/${getFacilityTypeUrlSlug(facility.facilityType)}` }]
              : []),
            { name: facility.name },
          ]} />
        </>
      )}
      {/* 데스크톱: 히어로 (깨끗한 블루 그라데이션 헤더 — 대표 사진은 기본정보 상단에 별도 노출) — 모바일 미노출 */}
      {facility && (
        <div className="relative hidden overflow-hidden bg-gradient-to-r from-[#1e3a8a] via-[#2563eb] to-[#4338ca] lg:block">
          <div className="absolute inset-0 overflow-hidden pointer-events-none">
            <div className="absolute inset-0 opacity-[0.06]" style={{ backgroundImage: 'radial-gradient(circle, white 1px, transparent 1px)', backgroundSize: '20px 20px' }} />
          </div>
          <div className="relative max-w-6xl mx-auto px-6 py-10">
            <button onClick={smartBack} className="flex items-center gap-1.5 text-sm text-white/60 hover:text-white/90 transition-colors mb-4">
              <ArrowRightLeft size={16} /> 뒤로가기
            </button>
            <div className="flex items-start justify-between">
              <div>
                <div className="flex items-center gap-2.5 mb-3">
                  <span className="rounded-full bg-white/20 backdrop-blur-sm px-3 py-1 text-xs font-bold text-white">
                    {FACILITY_TYPE_LABELS[facility.facilityType as FacilityType] ?? facility.facilityType}
                  </span>
                  {facility.evaluationGrade && (
                    <span className="rounded-full bg-white/20 backdrop-blur-sm px-3 py-1 text-xs font-bold text-white">
                      {facility.evaluationGrade}등급
                    </span>
                  )}
                </div>
                {/* 평가 출처는 기관명 + 평가구분 + 급여종류까지, 다른 급여종류 평가면 차용 표기 (facility-detail-evaluation-spec.md §2, §2.5) */}
                {facility.evaluationGrade && facility.evaluationCategory && (
                  <p className="mb-2 text-xs text-white/70">
                    평가등급 출처: {buildEvaluationSourceLabel(facility.evaluationCategory, facility.evaluationServiceKind, facility.facilityType)}
                  </p>
                )}
                {/* SEO: h1은 페이지당 1개만. 실제 h1은 모바일 헤더(아래)에 두고,
                    데스크톱 히어로 제목은 p로 유지(반응형이라 HTML엔 둘 다 존재 → h1 중복 방지) */}
                <p className="text-3xl font-bold text-white">{facility.name}</p>
                <div className="mt-3 flex items-center gap-4 text-sm text-white/70">
                  <span className="flex items-center gap-1.5"><MapPin size={14} /> {facility.address}</span>
                  {facility.phone && (
                    <a
                      href={`tel:${facility.phone}`}
                      onClick={() => { logFacilityVisitEvent(facility.id, 'PHONE_CLICK'); trackCallFacility(facility.id) }}
                      className="flex items-center gap-1.5 text-white hover:text-white/90"
                    >
                      <Phone size={14} /> {facility.phone}
                    </a>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-1">
                <button
                  onClick={async () => {
                    const url = window.location.href
                    if (navigator.share) {
                      try { await navigator.share({ title: facility.name, url }) } catch {}
                    } else {
                      const ok = await copyToClipboard(url)
                      if (ok) toast.success('링크가 복사되었습니다')
                      else toast.error('복사에 실패했습니다. 주소창의 링크를 직접 복사해주세요')
                    }
                  }}
                  className="flex h-10 w-10 items-center justify-center rounded-full hover:bg-white/10 transition-colors"
                  aria-label="공유"
                >
                  <Share2 size={20} className="text-white/70" />
                </button>
                {isAuthenticated && (
                  <button
                    onClick={() => toggleBookmark(facilityId, isBookmarked)}
                    className="flex h-10 w-10 items-center justify-center rounded-full hover:bg-white/10 transition-colors"
                    aria-label={isBookmarked ? '북마크 해제' : '북마크 추가'}
                  >
                    <Heart size={22} className={isBookmarked ? 'fill-red-400 text-red-400' : 'text-white/50'} />
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* 모바일: 기존 헤더 유지 — 데스크톱 미노출 */}
      <div className="lg:hidden">
        <Header
          title="시설 상세"
          showBack
          rightAction={
            <div className="flex items-center gap-1">
              <button
                onClick={async () => {
                  const url = window.location.href
                  if (navigator.share) {
                    try {
                      await navigator.share({ title: facility?.name ?? '시설 정보', url })
                    } catch { /* 취소 */ }
                  } else {
                    const ok = await copyToClipboard(url)
                    if (ok) toast.success('링크가 복사되었습니다')
                    else toast.error('복사에 실패했습니다. 주소창의 링크를 직접 복사해주세요')
                  }
                }}
                className="flex h-10 w-10 items-center justify-center rounded-full hover:bg-cb-gray-100 transition-colors"
                aria-label="공유"
              >
                <Share2 size={20} className="text-cb-gray-500" />
              </button>
              {isAuthenticated && (
                <button
                  onClick={() => toggleBookmark(facilityId, isBookmarked)}
                  className="flex h-10 w-10 items-center justify-center rounded-full hover:bg-cb-gray-100 transition-colors"
                  aria-label={isBookmarked ? '북마크 해제' : '북마크 추가'}
                >
                  <Heart
                    size={22}
                    className={isBookmarked ? 'fill-cb-warm text-cb-warm' : 'text-cb-gray-400'}
                  />
                </button>
              )}
            </div>
          }
        />
      </div>

      {/* pb-24: 모바일 하단 고정 CTA 바가 마지막 콘텐츠를 가리지 않도록 확보 */}
      <main className="flex-1 mx-auto w-full max-w-[480px] px-[var(--spacing-page-x)] py-[var(--spacing-section)] pb-24 lg:mx-0 lg:max-w-none lg:px-0 lg:py-0 lg:pb-0">
        {/* 로딩 */}
        {isLoading && <DetailSkeleton />}

        {/* 에러 */}
        {isError && (
          <EmptyState
            message="시설 정보를 불러오지 못했어요"
            description={
              error instanceof Error
                ? error.message
                : '잠시 후 다시 시도해주세요.'
            }
            actionLabel="다시 시도"
            onAction={() => window.location.reload()}
          />
        )}

        {/* 비활성 시설 안내 — 애니메이션 없이 즉시 노출 (ssr-spec §15.3) */}
        {facility && isInactive && (
          <div className="lg:mx-auto lg:max-w-6xl lg:px-6 lg:pt-6">
            <div
              role="note"
              className="mb-4 flex items-start gap-2 rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm leading-relaxed text-amber-900 lg:mb-0"
            >
              <AlertTriangle size={18} className="mt-0.5 flex-shrink-0 text-amber-600" aria-hidden="true" />
              <p>
                현재 운영 정보가 확인되지 않는 시설입니다. 폐업했거나 정보가 바뀌었을 수 있으니 방문 전 시설에 직접 확인해 주세요.
              </p>
            </div>
          </div>
        )}

        {/* 시설 상세 데이터 */}
        {facility && (
          <motion.div
            className="flex flex-col gap-5 lg:max-w-6xl lg:mx-auto lg:gap-0 lg:px-6 lg:py-8"
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
          >
            {/* 데이터 분석 요약 — 시설마다 고유한 문단(SEO/애드센스 thin content 해결) */}
            <FacilityAnalysisSection facility={facility} />

            {/* 시설 헤더: 모바일에서만 표시 (데스크톱은 위 블루 히어로에서 표시) */}
            <section className="rounded-2xl overflow-hidden lg:hidden">
                {facilityPhotos && facilityPhotos.length > 0 ? (
                  <PhotoGallery photos={facilityPhotos} facilityName={facility.name} />
                ) : (
                  <FacilityHeroImage url={facility.thumbnailUrl} name={facility.name} />
                )}
                <div className="p-5" style={{ background: facility.thumbnailUrl ? 'white' : 'var(--gradient-result)' }}>
                  <div className="flex items-center gap-2.5 flex-wrap mb-3">
                    <Badge variant="type" className="text-body-sm px-3 py-1">
                      {FACILITY_TYPE_LABELS[facility.facilityType as FacilityType] ?? facility.facilityType}
                    </Badge>
                    {facility.evaluationGrade && (
                      <Badge variant="grade" grade={facility.evaluationGrade as EvaluationGrade} className="text-body-sm px-3 py-1" />
                    )}
                  </div>
                  {facility.evaluationGrade && facility.evaluationCategory && (
                    <p className="-mt-1 mb-3 text-caption text-cb-gray-500">
                      평가등급 출처: {buildEvaluationSourceLabel(facility.evaluationCategory, facility.evaluationServiceKind, facility.facilityType)}
                    </p>
                  )}
                  <div className="flex items-center gap-2">
                    <h1 className="text-h1 font-bold text-cb-gray-900">{facility.name}</h1>
                    {facility.premiumPlan && (
                      <Badge variant="premium" />
                    )}
                  </div>
                  <div className="mt-3 flex flex-col gap-1.5">
                    <div className="flex items-center gap-2 text-body-sm text-cb-gray-600">
                      <MapPin size={16} className="flex-shrink-0 text-cb-primary" />
                      <span>{facility.address}</span>
                    </div>
                    {facility.phone && (
                      <div className="flex items-center gap-2 text-body-sm text-cb-gray-600">
                        <Phone size={16} className="flex-shrink-0 text-cb-secondary" />
                        <a
                          href={`tel:${facility.phone}`}
                          onClick={() => { logFacilityVisitEvent(facility.id, 'PHONE_CLICK'); trackCallFacility(facility.id) }}
                          className="text-cb-primary font-medium underline underline-offset-2"
                        >{facility.phone}</a>
                      </div>
                    )}
                  </div>
                </div>
              </section>

            {/* 탭 그룹 */}
            {/* 프로모션 슬롯 (모바일 본문 중간) — 모바일 전용 slotKey.
                애드핏 fallback 이 PC 세로배너(160x600) 대신 모바일용 320x100 을 쓰도록 분리했다
                (세로 긴 배너가 모바일 본문을 과점하는 문제 해소, 페이지당 각 광고단위 1회 삽입).
                하우스 배너를 모바일에도 노출하려면 이 slotKey(facility_detail_side_mobile)로 별도 등록 필요. */}
            <div className="lg:hidden">
              <PromotionSlot slotKey="facility_detail_side_mobile" className="my-2" />
            </div>

            <div className="lg:sticky lg:top-16 lg:z-10 lg:bg-white lg:border-b lg:mb-6">
              <TabGroup
                tabs={getTabs(!!facilityPhotos && facilityPhotos.length > 0)}
                activeTab={activeTab}
                onTabChange={setActiveTab}
              />
            </div>

            {/* Desktop: 2-column layout / Mobile: single column */}
            <div className="lg:flex lg:gap-8">
              {/* Left: tab content */}
              <div className="lg:flex-1 lg:min-w-0 lg:flex lg:flex-col lg:gap-5">
                {/* 기본정보 탭 */}
                <AnimatePresence mode="wait">
                  <TabPanel tabKey="info" activeTab={activeTab}>
                    <motion.div
                      key="info"
                      className="flex flex-col gap-4"
                      initial={{ opacity: 0, y: 8 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -8 }}
                      transition={{ duration: 0.2 }}
                    >
                      {/* 대표 사진 (데스크톱 — 기본정보 상단에 대표 1장만 정사각형으로. 나머지는 사진 탭) */}
                      {facilityPhotos && facilityPhotos.length > 0 && (() => {
                        const mainPhoto = facilityPhotos.find((p: any) => p.sortOrder === 0) ?? facilityPhotos[0]
                        return (
                          <button
                            type="button"
                            onClick={() => setActiveTab('photos')}
                            className="group relative hidden w-full overflow-hidden rounded-2xl shadow-sm lg:block"
                            aria-label="시설 사진 모두 보기"
                          >
                            <img
                              src={toPhotoUrl(mainPhoto.url)}
                              alt={`${facility.name} 대표 사진`}
                              className="aspect-video w-full object-cover transition-transform duration-300 group-hover:scale-105"
                            />
                            {facilityPhotos.length > 1 && (
                              <span className="absolute bottom-2 right-2 rounded-full bg-black/55 px-2.5 py-1 text-[11px] font-semibold text-white">
                                사진 {facilityPhotos.length}장
                              </span>
                            )}
                          </button>
                        )
                      })()}

                      {/* 시설 소개 (운영자 직접 입력, description 이 있을 때만 표시) */}
                      {facility.description && facility.description.trim() !== '' && (
                        <Card>
                          <h4 className="text-body font-bold text-cb-gray-900 mb-3 flex items-center gap-2">
                            <MessageSquare className="h-4.5 w-4.5 text-cb-primary" size={18} />
                            시설 소개
                          </h4>
                          <p className="text-body-sm text-cb-gray-700 leading-relaxed whitespace-pre-line">
                            {facility.description}
                          </p>
                        </Card>
                      )}

                      {/* 정원/현원 (데이터가 있을 때만 표시) */}
                      {facility.capacity != null && facility.capacity > 0 && (
                        <Card>
                          <h4 className="text-body font-bold text-cb-gray-900 mb-4 flex items-center gap-2">
                            <Users className="h-4.5 w-4.5 text-cb-primary" size={18} />
                            정원 현황
                          </h4>
                          <ProgressBar
                            value={facility.currentCount ?? 0}
                            max={facility.capacity}
                            label="현재 입소 현황"
                            valueText={`정원 ${facility.capacity}명 중 ${facility.currentCount ?? 0}명 (여유 ${Math.max(0, facility.capacity - (facility.currentCount ?? 0))}명)`}
                            color={
                              facility.currentCount != null && facility.currentCount / facility.capacity > 0.9
                                ? 'accent'
                                : 'primary'
                            }
                            size="lg"
                          />
                        </Card>
                      )}

                      {/* 인력현황 + 요양보호사 비율 */}
                      {facility.staffInfo && (
                        <StaffInfoCard staffInfo={facility.staffInfo} capacity={facility.capacity} />
                      )}

                      {/* 프로그램 운영사항 */}
                      {facility.programs && facility.programs.length > 0 && (
                        <Card>
                          <h4 className="text-body font-bold text-cb-gray-900 mb-4 flex items-center gap-2">
                            <BookOpen className="h-4.5 w-4.5 text-cb-primary" size={18} />
                            프로그램 ({facility.programs.length}개)
                          </h4>
                          <div className="flex flex-col gap-2">
                            {facility.programs.map((pgm, idx) => (
                              <div
                                key={idx}
                                className="flex items-start gap-3 rounded-xl bg-cb-gray-50 px-3.5 py-2.5"
                              >
                                <span className={`flex-shrink-0 mt-0.5 rounded-md px-1.5 py-0.5 text-[10px] font-bold ${
                                  pgm.programType === '1' ? 'bg-blue-100 text-blue-700' :
                                  pgm.programType === '2' ? 'bg-green-100 text-green-700' :
                                  'bg-amber-100 text-amber-700'
                                }`}>
                                  {pgm.programTypeName}
                                </span>
                                <div className="flex-1 min-w-0">
                                  <p className="text-body-sm font-medium text-cb-gray-900 truncate">
                                    {pgm.programName}
                                  </p>
                                  {pgm.runPlace && (
                                    <p className="text-caption text-cb-gray-400 mt-0.5 truncate">
                                      {pgm.runPlace}
                                    </p>
                                  )}
                                </div>
                              </div>
                            ))}
                          </div>
                        </Card>
                      )}

                      {/* 시설 정보 (아이콘 + 라벨/값 레이아웃 개선) */}
                      <Card>
                        <h4 className="text-body font-bold text-cb-gray-900 mb-4 flex items-center gap-2">
                          <Building2 className="h-4.5 w-4.5 text-cb-accent" size={18} />
                          시설 정보
                        </h4>
                        <div className="flex flex-col gap-3.5">
                          <InfoRow
                            icon={<Building2 size={16} className="text-cb-gray-400" />}
                            label="시설 유형"
                            value={FACILITY_TYPE_LABELS[facility.facilityType as FacilityType] ?? facility.facilityType}
                          />
                          {facility.establishedDate && (
                            <InfoRow
                              icon={<Calendar size={16} className="text-cb-gray-400" />}
                              label="설립일"
                              value={facility.establishedDate}
                            />
                          )}
                          {facility.hasDementiaCare && (
                            <InfoRow
                              icon={<Shield size={16} className="text-cb-secondary" />}
                              label="치매전담"
                              value="운영"
                              valueClass="text-cb-secondary font-semibold"
                            />
                          )}
                          {facility.establishmentType && (
                            <InfoRow
                              icon={<Building2 size={16} className="text-cb-gray-400" />}
                              label="설립유형"
                              value={facility.establishmentType}
                            />
                          )}
                        </div>
                      </Card>

                      {/* 시설현황 (room_info JSON) */}
                      {facility.roomInfo && <RoomInfoCard roomInfo={facility.roomInfo} />}

                      {/* 요양병원 진료 정보 — 요양병원 유형이고 hospitalDetail 있을 때만 노출.
                          요양원·주간보호 등 비병원 유형은 hospitalDetail=null 이라 미노출. */}
                      {facility.facilityType === 'NURSING_HOSPITAL' && facility.hospitalDetail && (
                        <HospitalInfoSection detail={facility.hospitalDetail} />
                      )}

                      {/* 대기인원 정보 */}
                      {facility.waitingCount != null && facility.waitingCount > 0 && (
                        <Card className="border-l-4 border-cb-accent">
                          <div className="flex items-center justify-between">
                            <span className="text-body font-bold text-cb-gray-900">대기 인원</span>
                            <span className="text-h2 font-bold text-cb-accent">{facility.waitingCount}명</span>
                          </div>
                        </Card>
                      )}

                      {/* 상담/전화 문의 (모바일만) */}
                      <div className="flex gap-2 lg:hidden">
                        {facility.phone && (
                          <Button
                            variant="secondary"
                            size="lg"
                            className="flex-1"
                            onClick={handlePhoneClick}
                          >
                            <Phone size={18} />
                            전화 문의
                          </Button>
                        )}
                        {facility.premiumPlan && (
                          <Button
                            variant="primary"
                            size="lg"
                            className="flex-1"
                            onClick={() => setIsConsultationOpen(true)}
                          >
                            <BookOpen size={18} />
                            입소 상담 요청
                          </Button>
                        )}
                      </div>

                      {/* 비용 시뮬레이터 연결 (모바일만) */}
                      <div className="lg:hidden">
                        {COST_SUPPORTED_TYPES.has(facility.facilityType as string) ? (
                          <Button
                            variant="secondary"
                            size="lg"
                            fullWidth
                            onClick={() => navigate(`/cost-simulator?facilityId=${facility.id}&facilityName=${encodeURIComponent(facility.name)}&facilityType=${facility.facilityType}&capacity=${facility.capacity ?? 0}`)}
                          >
                            <Banknote size={18} />
                            이 시설로 비용 계산하기
                          </Button>
                        ) : (
                          <div className="rounded-2xl bg-cb-gray-50 px-4 py-3 text-center text-body-sm text-cb-gray-400">
                            이 시설 유형은 비용 계산을 지원하지 않습니다
                          </div>
                        )}
                      </div>

                      {/* 다른 시설과 비교 (모바일만) */}
                      <Button
                        variant="ghost"
                        size="lg"
                        fullWidth
                        onClick={() => {
                          navigate(`/facilities?compareWith=${facility.id}`)
                        }}
                        className="flex items-center justify-center gap-2 lg:hidden"
                      >
                        <ArrowRightLeft size={18} />
                        다른 시설과 비교하기
                      </Button>

                      {/* 체크리스트 진입 CTA (모바일만) — plan §5.1 */}
                      <div className="lg:hidden">
                        <ChecklistPromoCard
                          variant="facility-detail"
                          facilityName={facility.name}
                          facilityId={facility.id}
                        />
                      </div>

                      {/* 돌봄일지 유도 카드 */}
                      <div className="rounded-2xl bg-purple-50 border border-purple-100 p-5">
                        <p className="text-sm font-bold text-purple-900">이 시설에 부모님이 계시나요?</p>
                        <p className="mt-1 text-xs text-purple-600/80">돌봄일지를 시작하면 매일 건강을 기록할 수 있어요</p>
                        <button
                          onClick={() => navigate('/mypage/care-log')}
                          className="mt-3 w-full rounded-xl bg-purple-600 px-4 py-2.5 text-sm font-bold text-white hover:bg-purple-700 transition-colors"
                        >
                          돌봄일지 시작하기
                        </button>
                      </div>
                    </motion.div>
                  </TabPanel>
                </AnimatePresence>

                {/* 사진 탭 (승인된 사진 있으면 무료 시설도 노출) */}
                {facilityPhotos && facilityPhotos.length > 0 && (
                  <TabPanel tabKey="photos" activeTab={activeTab}>
                    <motion.div
                      key="photos"
                      className="flex flex-col gap-4"
                      initial={{ opacity: 0, y: 8 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.2 }}
                    >
                      <p className="text-body-sm text-cb-gray-500">시설 사진 {facilityPhotos.length}장</p>
                      <PhotoGridGallery photos={facilityPhotos} facilityName={facility.name} />
                    </motion.div>
                  </TabPanel>
                )}

                {/* 비용 탭 */}
                <TabPanel tabKey="cost" activeTab={activeTab}>
                  <motion.div
                    key="cost"
                    className="flex flex-col gap-4"
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -8 }}
                    transition={{ duration: 0.2 }}
                  >
                    {/* 비급여 항목 (공공데이터 실제 데이터) */}
                    {facility.nonCoveredCosts && facility.nonCoveredCosts.length > 0 ? (
                      <Card>
                        <h4 className="text-body font-bold text-cb-gray-900 mb-1 flex items-center gap-2">
                          <Banknote className="h-4.5 w-4.5 text-cb-accent" size={18} />
                          비급여 항목
                        </h4>
                        <p className="text-caption text-cb-gray-400 mb-4">
                          {facility.nonCoveredCosts.some((c) => c.source === 'MANUAL')
                            ? '공공데이터(NHIS) 신고 항목과 시설이 직접 등록한 항목이 함께 표시됩니다. 공공데이터는 시설이 신고한 원본이라 중복 등록이 포함될 수 있습니다.'
                            : '공공데이터(NHIS)에 등록된 그대로의 비급여 항목입니다. 시설이 신고한 원본 데이터로, 중복 등록이 포함될 수 있습니다.'}
                        </p>

                        <div className="flex flex-col gap-2">
                          {facility.nonCoveredCosts.map((cost, idx) => (
                            <div
                              key={`${cost.itemCode}-${idx}`}
                              className="flex items-center justify-between rounded-xl bg-cb-gray-50 px-4 py-3"
                            >
                              <div className="flex flex-col">
                                <span className="text-body-sm font-semibold text-cb-gray-900 flex items-center gap-1.5">
                                  {cost.itemName}
                                  {/* 출처를 숨기면 "공공데이터 원본"이라는 안내가 거짓이 된다. */}
                                  {cost.source === 'MANUAL' && (
                                    <span className="rounded bg-cb-primary/10 px-1.5 py-0.5 text-[10px] font-semibold text-cb-primary">
                                      시설 등록
                                    </span>
                                  )}
                                </span>
                                {cost.calculationBasis && (
                                  <span className="text-caption text-cb-gray-400 mt-0.5">
                                    {cost.calculationBasis}
                                  </span>
                                )}
                              </div>
                              <div className="text-right">
                                <span className="text-body font-bold text-cb-primary text-amount">
                                  {(cost.amount * 30).toLocaleString()}원
                                </span>
                                <span className="block text-caption text-cb-gray-400">
                                  일 {cost.amount.toLocaleString()}원
                                </span>
                              </div>
                            </div>
                          ))}
                        </div>

                        {/* 합계 */}
                        <div className="mt-3 flex items-center justify-between rounded-xl bg-cb-primary-light px-4 py-3">
                          <div className="flex flex-col">
                            <span className="text-body font-bold text-cb-gray-900">월 비급여 합계</span>
                            <span className="text-caption text-cb-gray-400 mt-0.5">
                              공공데이터 등록 항목 단순 합계 (중복 포함)
                            </span>
                          </div>
                          <span className="text-h3 font-bold text-cb-primary text-amount">
                            {facility.nonCoveredCosts
                              .reduce((sum, c) => sum + c.amount * 30, 0)
                              .toLocaleString()}원
                          </span>
                        </div>
                      </Card>
                    ) : (
                      <Card className="text-center py-8">
                        <Banknote size={36} className="mx-auto text-cb-gray-300 mb-2" />
                        <p className="text-body-sm text-cb-gray-500">
                          이 시설의 비급여 정보가 아직 수집되지 않았어요
                        </p>
                        <p className="text-caption text-cb-gray-400 mt-1">
                          전국 평균 비용으로 계산할 수 있어요
                        </p>
                      </Card>
                    )}

                    {/* 비용 시뮬레이터 연결 */}
                    {COST_SUPPORTED_TYPES.has(facility.facilityType as string) ? (
                      <Button
                        variant="primary"
                        size="lg"
                        fullWidth
                        onClick={() => {
                          const hasRealCost = facility.nonCoveredCosts && facility.nonCoveredCosts.length > 0
                          const params = new URLSearchParams({
                            facilityName: facility.name,
                            facilityType: facility.facilityType as string,
                            capacity: String(facility.capacity ?? 0),
                          })
                          if (hasRealCost) {
                            params.set('facilityId', String(facility.id))
                          }
                          navigate(`/cost-simulator?${params.toString()}`)
                        }}
                      >
                        <Calculator size={18} />
                        {facility.nonCoveredCosts && facility.nonCoveredCosts.length > 0
                          ? '이 시설 실제 비용으로 계산하기'
                          : '전국 평균 비용으로 계산하기'}
                      </Button>
                    ) : (
                      <div className="rounded-2xl bg-cb-gray-50 px-4 py-3 text-center text-body-sm text-cb-gray-400">
                        이 시설 유형은 비용 계산을 지원하지 않습니다
                      </div>
                    )}
                  </motion.div>
                </TabPanel>

                {/* 후기 탭 */}
                <TabPanel tabKey="review" activeTab={activeTab}>
                  <FacilityReviewTab facilityId={facility.id} />

                  {/* 관련 블로그 소식 */}
                  {blogPosts && blogPosts.length > 0 && (
                    <section className="mt-8 pt-6 border-t border-gray-100">
                      <div className="flex items-center justify-between mb-4">
                        <h3 className="text-base font-bold text-gray-900 flex items-center gap-2">
                          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" className="text-green-500">
                            <rect x="3" y="3" width="18" height="18" rx="4" stroke="currentColor" strokeWidth="2"/>
                            <path d="M8 8h3v8H8V8zm5 3h3v5h-3v-5z" fill="currentColor"/>
                          </svg>
                          관련 블로그 소식
                        </h3>
                        <a
                          href={`https://search.naver.com/search.naver?where=blog&query=${encodeURIComponent('"' + facility.name + '"')}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-xs text-gray-400 hover:text-gray-600 flex items-center gap-1"
                        >
                          더보기
                          <ExternalLink size={12} />
                        </a>
                      </div>
                      <div className="flex gap-3 overflow-x-auto pb-2 snap-x snap-mandatory scrollbar-hide lg:grid lg:grid-cols-3 lg:gap-4 lg:overflow-x-visible lg:pb-0 lg:snap-none">
                        {blogPosts.slice(0, 5).map((post, i) => (
                          <a
                            key={i}
                            href={post.link}
                            target="_blank"
                            rel="noopener noreferrer"
                            className={`rounded-2xl border border-gray-200 bg-white p-4 hover:border-gray-300 hover:shadow-sm transition-all min-w-[260px] snap-start flex-shrink-0 lg:min-w-0 lg:flex-shrink ${
                              i >= 3 ? 'lg:hidden' : ''
                            }`}
                          >
                            <p className="text-sm font-bold text-gray-900 line-clamp-1 mb-1.5">{post.title}</p>
                            <p className="text-xs text-gray-500 line-clamp-2 mb-3 leading-relaxed">{post.description}</p>
                            <div className="flex items-center justify-between">
                              <span className="text-[11px] text-gray-400 truncate max-w-[120px]">{post.bloggerName}</span>
                              <span className="text-[11px] text-gray-400">{post.postDate}</span>
                            </div>
                          </a>
                        ))}
                      </div>
                      <p className="text-[11px] text-gray-300 mt-2 text-center">네이버 블로그 검색 결과입니다</p>
                    </section>
                  )}
                </TabPanel>

                {/* 평가 탭 */}
                <TabPanel tabKey="evaluation" activeTab={activeTab}>
                  <motion.div
                    key="evaluation"
                    className="flex flex-col gap-4"
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.2 }}
                  >
                    <Card>
                      <EvaluationChart
                        grade={(facility.evaluationGrade as EvaluationGrade) ?? undefined}
                        totalScore={facility.evaluationScore ?? 0}
                        items={evaluationScoreAreas(facility)}
                      />
                      {facility.evaluationDate && (
                        <p className="mt-5 text-caption text-cb-gray-400 text-center">
                          평가일: {facility.evaluationDate}
                        </p>
                      )}
                      <p className="mt-1 text-caption text-cb-gray-400 text-center">
                        상세 항목별 점수는 {buildEvaluationSourcePhrase(facility.evaluationCategory, facility.evaluationServiceKind, facility.facilityType)} 데이터 기준입니다.
                      </p>
                    </Card>
                  </motion.div>
                </TabPanel>

                {/* 위치 탭 */}
                <TabPanel tabKey="location" activeTab={activeTab}>
                  <motion.div
                    key="location"
                    className="flex flex-col gap-4"
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.2 }}
                  >
                    <Card>
                      <h4 className="text-body font-bold text-cb-gray-900 mb-3 flex items-center gap-2">
                        <MapPin size={18} className="text-cb-primary" />
                        주소
                      </h4>
                      <div className="flex items-start gap-2">
                        <p className="text-body text-cb-gray-700">{facility.address}</p>
                      </div>
                    </Card>

                    {/* 지도 (네이버 지도 SDK) */}
                    {facility.latitude && facility.longitude ? (
                      <Card noPadding className="overflow-hidden rounded-2xl">
                        <div
                          ref={locationMapRef}
                          className="w-full h-48 lg:h-72"
                        />
                      </Card>
                    ) : (
                      <Card className="flex items-center justify-center py-12" style={{ background: 'var(--gradient-card-cool)' }}>
                        <div className="text-center">
                          <MapPin size={36} className="mx-auto text-cb-gray-300" />
                          <p className="mt-2 text-body-sm text-cb-gray-400">
                            위치 정보가 없어요
                          </p>
                        </div>
                      </Card>
                    )}

                    {/* 길찾기 버튼 (네이버 / 카카오) */}
                    <div className="grid grid-cols-2 gap-2">
                      <Button
                        variant="primary"
                        size="lg"
                        fullWidth
                        onClick={handleNaverDirections}
                        className="flex items-center justify-center gap-2"
                      >
                        <ExternalLink size={18} />
                        네이버 길찾기
                      </Button>
                      <Button
                        variant="secondary"
                        size="lg"
                        fullWidth
                        onClick={handleKakaoDirections}
                        className="flex items-center justify-center gap-2"
                      >
                        <ExternalLink size={18} />
                        카카오 길찾기
                      </Button>
                    </div>

                    {/* 추가 정보: 홈페이지, 교통, 주차 (NHIS 공공데이터) */}
                    {(facility.homepageUrl || facility.transportation || facility.parkingInfo) && (
                      <Card>
                        <h4 className="text-body font-bold text-cb-gray-900 mb-4 flex items-center gap-2">
                          <Globe className="h-4.5 w-4.5 text-cb-primary" size={18} />
                          추가 정보
                        </h4>
                        <div className="flex flex-col gap-3.5">
                          {facility.homepageUrl && (
                            <HomepageRow url={facility.homepageUrl} />
                          )}
                          {facility.transportation && (
                            <InfoBlock
                              icon={<Car size={16} className="text-cb-gray-400" />}
                              label="교통편"
                              value={facility.transportation}
                            />
                          )}
                          {facility.parkingInfo && (
                            <InfoRow
                              icon={<Car size={16} className="text-cb-gray-400" />}
                              label="주차"
                              value={facility.parkingInfo}
                            />
                          )}
                        </div>
                      </Card>
                    )}
                  </motion.div>
                </TabPanel>
              </div>

              {/* Right: CTA sidebar (데스크톱만) */}
              <aside className="hidden w-80 flex-shrink-0 lg:block">
                  <div className="sticky top-32 space-y-3">
                    {facility.premiumPlan ? (
                      <>
                        <Button
                          variant="primary"
                          size="lg"
                          fullWidth
                          onClick={() => setIsConsultationOpen(true)}
                        >
                          <BookOpen size={18} />
                          입소 상담 요청
                        </Button>
                        {facility.phone && (
                          <Button
                            variant="secondary"
                            size="lg"
                            fullWidth
                            onClick={handlePhoneClick}
                          >
                            <Phone size={18} />
                            전화 문의
                          </Button>
                        )}
                      </>
                    ) : facility.phone ? (
                      <Button
                        variant="primary"
                        size="lg"
                        fullWidth
                        onClick={handlePhoneClick}
                      >
                        <Phone size={18} />
                        전화 문의하기
                      </Button>
                    ) : null}
                    {COST_SUPPORTED_TYPES.has(facility.facilityType as string) && (
                      <Button
                        variant="secondary"
                        size="lg"
                        fullWidth
                        onClick={() => navigate(`/cost-simulator?facilityId=${facility.id}&facilityName=${encodeURIComponent(facility.name)}&facilityType=${facility.facilityType}&capacity=${facility.capacity ?? 0}`)}
                      >
                        <Banknote size={18} />
                        비용 계산하기
                      </Button>
                    )}
                    <Button
                      variant="ghost"
                      size="lg"
                      fullWidth
                      onClick={() => {
                        navigate(`/facilities?compareWith=${facility.id}`)
                      }}
                      className="flex items-center justify-center gap-2"
                    >
                      <ArrowRightLeft size={18} />
                      다른 시설과 비교하기
                    </Button>
                    {isAuthenticated && (
                      <Button
                        variant="ghost"
                        size="lg"
                        fullWidth
                        onClick={() => toggleBookmark(facilityId, isBookmarked)}
                        className="flex items-center justify-center gap-2"
                      >
                        <Heart
                          size={18}
                          className={isBookmarked ? 'fill-cb-warm text-cb-warm' : undefined}
                        />
                        {isBookmarked ? '관심 시설 해제' : '관심 시설 추가'}
                      </Button>
                    )}
                    {/* 체크리스트 진입 CTA — plan §5.1 */}
                    <ChecklistPromoCard
                      variant="facility-detail"
                      facilityName={facility.name}
                      facilityId={facility.id}
                    />
                    {/* 돌봄일지 유도 카드 */}
                    <div className="rounded-2xl bg-purple-50 border border-purple-100 p-5 mt-2">
                      <p className="text-sm font-bold text-purple-900">이 시설에 부모님이 계시나요?</p>
                      <p className="mt-1 text-xs text-purple-600/80">돌봄일지를 시작하면 매일 건강을 기록할 수 있어요</p>
                      <button
                        onClick={() => navigate('/mypage/care-log')}
                        className="mt-3 w-full rounded-xl bg-purple-600 px-4 py-2.5 text-sm font-bold text-white hover:bg-purple-700 transition-colors"
                      >
                        돌봄일지 시작하기
                      </button>
                    </div>
                    {/* 프로모션 슬롯: facility_detail_side */}
                    {/* 시설상세 우측 광고 슬롯 — 하우스 배너 등록 시 그것, 없으면 애드핏(160x600) 자동 fallback.
                        애드핏 매핑은 features/promotion/adfitSlots.ts(facility_detail_side) 참조. */}
                    <PromotionSlot slotKey="facility_detail_side" className="mt-2" />
                  </div>
                </aside>
            </div>

            {/* 자주 묻는 질문 — 시설 데이터 기반 Q&A + FAQPage JSON-LD (질문 2개 미만이면 전체 미노출) */}
            <FacilityFAQSection facility={facility} />

            {/* 시설 운영자 입점 안내 배너 — 미입점 시설에만 노출 */}
            {facility && !facility.hasFacilityManager && (
              <div className="mt-10 rounded-2xl border border-slate-200 bg-slate-50 px-5 py-5 sm:px-6 sm:py-6">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                  <div>
                    <div className="text-sm font-bold text-slate-900">
                      이 시설을 운영하시나요?
                    </div>
                    <p className="mt-1 text-sm text-slate-600 leading-relaxed">
                      보호자에게 직접 사진과 소개, 상담 응답을 보여드릴 수 있습니다. 무료로 입점하세요.
                    </p>
                  </div>
                  <a
                    href={`https://biz.carebalance.co.kr/facility/signup?facilityId=${facility.id}`}
                    target="_blank"
                    rel="nofollow noopener noreferrer"
                    className="inline-flex items-center justify-center self-start sm:self-auto px-5 h-11 text-sm font-bold text-white bg-slate-900 hover:bg-slate-800 rounded-xl transition flex-shrink-0"
                  >
                    무료로 시설 등록하기
                  </a>
                </div>
              </div>
            )}

            {/* 같은 지역 시설 — 내부링크(SEO 크롤 경로) */}
            {facility && <RelatedFacilitiesSection facility={facility} />}

          </motion.div>
        )}
      </main>

      {/* 모바일 하단 고정 CTA — 데스크톱은 우측 sticky 사이드바가 같은 역할을 한다.
          모바일에는 그런 게 없어서 전화·상담 버튼이 기본정보 탭 맨 아래(약 1,868px, 2.2 화면)에
          있었고, GA4 상 스크롤한 사용자가 36.6%뿐이라 절반 이상이 버튼 근처도 못 갔다.
          (2026-08-27 라이브 실측) */}
      {facility && (
        <MobileStickyCta
          facility={facility}
          onConsultation={() => setIsConsultationOpen(true)}
          onPhone={handlePhoneClick}
          onCostSimulator={() =>
            navigate(
              `/cost-simulator?facilityId=${facility.id}&facilityName=${encodeURIComponent(facility.name)}&facilityType=${facility.facilityType}&capacity=${facility.capacity ?? 0}`
            )
          }
        />
      )}

      {/* 입소 상담 요청 모달 */}
      {facility && (
        <ConsultationModal
          isOpen={isConsultationOpen}
          onClose={() => setIsConsultationOpen(false)}
          facilityId={facility.id}
          facilityName={facility.name}
        />
      )}
    </>
  )
}

/**
 * 모바일 하단 고정 CTA 바.
 *
 * 데스크톱은 우측 sticky 사이드바가 전화·상담·비용계산을 항상 띄워 주는데,
 * 모바일에는 대응물이 없었다. 같은 버튼들이 기본정보 탭 맨 아래에만 있어
 * 라이브 실측(390x844)에서 전화 버튼이 1,868px — 2.2 화면 아래였다.
 * GA4 7일치에서 스크롤 이벤트가 발생한 사용자가 36.6%뿐이므로,
 * 나머지 63%는 버튼이 있는지조차 몰랐다는 뜻이다. (2026-08-27)
 *
 * 탭 아래 원래 버튼들은 그대로 둔다 — 스크롤해 내려온 사람의 문맥상 위치이고,
 * 지우면 그쪽 동선이 끊긴다.
 *
 * 배치: BottomNav(h-16 + safe-area) 바로 위에 fixed. z-30 은 BottomNav(z-40) 아래,
 * 상담 모달·사진 라이트박스(z-100) 아래라 가릴 일이 없다.
 * 사이드 배너는 xl 이상 데스크톱 전용이라 충돌하지 않는다.
 */
function MobileStickyCta({
  facility,
  onConsultation,
  onPhone,
  onCostSimulator,
}: {
  facility: FacilityDetail
  onConsultation: () => void
  onPhone: () => void
  onCostSimulator: () => void
}) {
  const hasPhone = !!facility.phone
  // 입소 상담은 프리미엄 시설만 받을 수 있다(무료 시설은 접수처가 없음).
  const hasConsultation = !!facility.premiumPlan
  const hasCost = COST_SUPPORTED_TYPES.has(facility.facilityType as string)

  // 연락 수단도 비용 계산도 없으면 바를 띄우지 않는다 — 빈 바가 화면만 잡아먹는다.
  if (!hasPhone && !hasConsultation && !hasCost) return null

  return (
    <div
      className="fixed inset-x-0 z-30 border-t border-cb-gray-200 bg-white/95 px-4 py-3 backdrop-blur lg:hidden"
      style={{ bottom: 'calc(4rem + env(safe-area-inset-bottom))' }}
    >
      <div className="flex gap-2">
        {hasConsultation && (
          <Button variant="primary" size="lg" className="flex-1" onClick={onConsultation}>
            <BookOpen size={18} />
            입소 상담
          </Button>
        )}
        {hasPhone && (
          <Button
            variant={hasConsultation ? 'secondary' : 'primary'}
            size="lg"
            className="flex-1"
            onClick={onPhone}
          >
            <Phone size={18} />
            전화 문의
          </Button>
        )}
        {/* 상담이 없는 무료 시설에서만 비용 계산을 두 번째 자리에 — 버튼 3개는 모바일에서 좁다.
            비용 계산기는 GA4 기준 전환율이 가장 높은 지면이다(55.6%). */}
        {!hasConsultation && hasCost && (
          <Button
            variant={hasPhone ? 'secondary' : 'primary'}
            size="lg"
            className="flex-1"
            onClick={onCostSimulator}
          >
            <Banknote size={18} />
            비용 계산
          </Button>
        )}
      </div>
    </div>
  )
}

/**
 * 시설 데이터로 만드는 고유 분석 문단(SEO/애드센스 thin content 해결).
 * 시설마다 값이 달라 페이지가 고유해진다. 규칙 기반(LLM 없음) — 표절 0, 결정적.
 * 영구지침: docs/plan/2026-06-08-adsense-content-strategy.md §5 Phase C
 */
/**
 * 시설의 평가 세부영역 점수 목록.
 * 2025년 정기평가부터 5영역 → 4영역(기관운영·수급자존중·서비스제공·서비스결과)으로 개편되어,
 * 신 4영역 값이 하나라도 있으면 그것을, 없으면 구 5영역을 반환한다. 값이 없는 영역은 제외한다.
 * (2025 시설의 구 5영역은 null 이므로, 필터하지 않으면 차트에 0점 항목이 뜬다 — 회귀 방지)
 */
function evaluationScoreAreas(f: FacilityDetail): { label: string; score: number }[] {
  const v2: { label: string; score: number | null }[] = [
    { label: '기관운영', score: f.scoreV2Operation },
    { label: '수급자존중', score: f.scoreV2Respect },
    { label: '서비스제공', score: f.scoreV2Service },
    { label: '서비스결과', score: f.scoreV2Result },
  ]
  const source = v2.some((a) => typeof a.score === 'number')
    ? v2
    : [
        { label: '기관 운영', score: f.scoreOperation },
        { label: '환경 및 안전', score: f.scoreEnvironment },
        { label: '수급자 권리', score: f.scoreRights },
        { label: '급여 제공과정', score: f.scoreProcess },
        { label: '급여 제공결과', score: f.scoreResult },
      ]
  return source.filter((a): a is { label: string; score: number } => typeof a.score === 'number')
}

/** 원 단위 금액 → 만원 축약 표기 (예: 480000 → "48만원", 8000 → "8,000원") */
function formatKrwCompact(amount: number): string {
  if (amount >= 10000) return `${Math.round(amount / 10000).toLocaleString()}만원`
  return `${amount.toLocaleString()}원`
}

/** staffInfo(JSON)에서 요양보호사 수 추출. 실패 시 null. */
function extractCareWorkers(staffInfo: string | null): number | null {
  if (!staffInfo) return null
  try {
    const parsed = typeof staffInfo === 'string' ? JSON.parse(staffInfo) : staffInfo
    if (parsed && typeof parsed === 'object') {
      const raw = parsed as Record<string, number>
      const v = raw['요양보호사'] ?? raw['careWorker'] ?? raw['caregiverCnt']
      return typeof v === 'number' ? v : null
    }
  } catch { /* ignore */ }
  return null
}

/** 지역 내 평가 백분위 노출 기준 — 상위 50% 이내만 표기(하위권 서열 표현으로 인한 시설 분쟁 방지). */
function isTopHalfPercentile(percentile: number | null | undefined): percentile is number {
  return percentile != null && percentile <= 50
}

function buildFacilityAnalysis(f: FacilityDetail): string {
  const typeLabel = FACILITY_TYPE_LABELS[f.facilityType as string] || '요양시설'
  const loc = formatRegionPath(f.addressSido, f.addressSigungu, f.addressDong)
  const s: string[] = []

  // 1) 정체성 + 운영형태 (제목에 시설명이 있으므로 "이 시설은"으로 자연스럽게)
  s.push(`이 시설은 ${loc ? `${loc}에 위치한 ` : ''}${typeLabel}${f.establishmentType ? `(${f.establishmentType} 운영)` : ''}입니다.`)

  // 2) 정원·가동률·대기
  if (typeof f.capacity === 'number' && f.capacity > 0) {
    let t = `정원 ${f.capacity}명 규모`
    if (typeof f.currentCount === 'number' && f.currentCount >= 0) {
      const occ = Math.round((f.currentCount / f.capacity) * 100)
      t += `로, 현재 ${f.currentCount}명이 입소해 가동률은 약 ${occ}%입니다`
    } else {
      t += `의 시설입니다`
    }
    if (typeof f.waitingCount === 'number' && f.waitingCount > 0) {
      t += `. 입소 대기자는 ${f.waitingCount}명입니다`
    }
    s.push(`${t}.`)
  }

  // 3) 평가등급 + 최고 점수 영역
  //    출처 기관은 국민건강보험공단(심평원 아님) + 평가구분·급여종류까지 명시
  //    (facility-detail-evaluation-spec.md §1, §2)
  if (f.evaluationGrade) {
    let t = `${buildEvaluationSourcePhrase(f.evaluationCategory, f.evaluationServiceKind, f.facilityType)}에서 ${f.evaluationGrade}등급`
    if (f.evaluationScore != null) t += `(${f.evaluationScore}점)`
    t += `을 받았습니다`
    const areas = evaluationScoreAreas(f)
    if (areas.length > 0) {
      const top = areas.reduce((m, a) => (a.score > m.score ? a : m))
      t += `. 특히 '${top.label}' 영역 점수가 상대적으로 높습니다`
    }
    s.push(`${t}.`)
  }

  // 4) 요양보호사 인력 비율
  const careWorkers = extractCareWorkers(f.staffInfo)
  if (careWorkers != null && careWorkers > 0 && f.capacity > 0) {
    const ratio = careWorkers / f.capacity
    const label = ratio >= 0.33 ? '우수한' : ratio >= 0.25 ? '적정한' : '보통 수준의'
    s.push(`요양보호사는 ${careWorkers}명으로, 정원 대비 ${label} 인력 비율을 갖추고 있습니다.`)
  }

  // 5) 비급여·프로그램·치매
  const nc = f.nonCoveredCosts?.length ?? 0
  const pg = f.programs?.length ?? 0
  const parts: string[] = []
  if (nc > 0) parts.push(`비급여 비용 항목 ${nc}개`)
  if (pg > 0) parts.push(`운영 프로그램 ${pg}개`)
  if (parts.length > 0) {
    s.push(`${parts.join('와 ')}의 상세 정보를 확인할 수 있습니다.`)
  }
  if (f.hasDementiaCare) s.push('치매 전담 케어가 가능한 시설입니다.')

  // 6) 지역 내 비교 (regionComparison — 집계 실패/시군구 없음이면 null. 전 필드 null-safe)
  const rc = f.regionComparison
  if (rc) {
    // 6-1) 지역 내 평가 — 상위 50% 이내일 때만 백분위를 노출한다.
    //      직접 순위("N곳 중 M위")나 하위권 백분위는 시설 서열화 분쟁 소지가 있어 표기하지 않는다
    //      (등급 자체는 별도 표기되므로 정보 은닉 아님). 그 외에는 지역 내 시설 수 맥락만 제공.
    if (rc.regionLabel && rc.typeLabel && rc.totalInRegion != null) {
      if (isTopHalfPercentile(rc.percentile)) {
        s.push(
          `${rc.regionLabel}의 ${rc.typeLabel} ${rc.totalInRegion}곳 중 평가 상위 ${rc.percentile}%입니다.`
        )
      } else {
        s.push(`${rc.regionLabel}에는 같은 유형의 ${rc.typeLabel} 시설이 ${rc.totalInRegion}곳 있습니다.`)
      }
    }
    // 6-2) 비급여 지역 평균 대비 — 예: "비급여 월 48만원으로 지역 평균(52만원)보다 8% 낮습니다."
    // 백엔드 rc 값은 이름과 달리 '일액' 합계라 비용 탭·FAQ와 동일하게 ×30 월 환산해 표기한다
    // (미환산 시 같은 페이지의 FAQ 금액과 30배 모순 — 2026-07-03 실측).
    if (rc.myMonthlyNonCovered != null && rc.avgMonthlyNonCovered != null && rc.diffPercent != null) {
      const my = formatKrwCompact(rc.myMonthlyNonCovered * 30)
      const avg = formatKrwCompact(rc.avgMonthlyNonCovered * 30)
      if (rc.diffPercent === 0) {
        s.push(`비급여 월 ${my}으로 지역 평균(${avg})과 비슷한 수준입니다.`)
      } else {
        s.push(
          `비급여 월 ${my}으로 지역 평균(${avg})보다 ${Math.abs(rc.diffPercent)}% ` +
          `${rc.diffPercent < 0 ? '낮습니다' : '높습니다'}.`
        )
      }
    }
  }

  return s.join(' ')
}

function FacilityAnalysisSection({ facility }: { facility: FacilityDetail }) {
  const text = buildFacilityAnalysis(facility)
  const occ = (typeof facility.capacity === 'number' && facility.capacity > 0
    && typeof facility.currentCount === 'number')
    ? Math.round((facility.currentCount / facility.capacity) * 100)
    : null
  return (
    <section className="px-5 pt-4 lg:px-0 lg:pt-0 lg:mb-6" aria-label="시설 데이터 요약">
      <div className="rounded-2xl border border-cb-gray-200 bg-white p-5">
        <h2 className="text-base font-bold text-cb-gray-900">데이터로 보는 {facility.name}</h2>
        <p className="mt-2 text-body-sm leading-relaxed text-cb-gray-700">{text}</p>
        {/* 핵심 지표 칩 */}
        <div className="mt-3 flex flex-wrap gap-2">
          {facility.evaluationGrade && (
            <Chip label="평가등급" value={`${facility.evaluationGrade}등급`} />
          )}
          {occ != null && <Chip label="가동률" value={`약 ${occ}%`} />}
          {typeof facility.capacity === 'number' && facility.capacity > 0 && (
            <Chip label="정원" value={`${facility.capacity}명`} />
          )}
          {isTopHalfPercentile(facility.regionComparison?.percentile) && (
            <Chip label="지역 내 평가" value={`상위 ${facility.regionComparison!.percentile}%`} />
          )}
          {facility.hasDementiaCare && <Chip label="특화" value="치매 전담" />}
        </div>
      </div>
    </section>
  )
}

function Chip({ label, value }: { label: string; value: string }) {
  return (
    <span className="inline-flex items-center gap-1 rounded-full bg-cb-primary/10 px-3 py-1 text-xs font-semibold text-cb-primary">
      <span className="text-cb-primary/60">{label}</span> {value}
    </span>
  )
}

/** '입소' 대신 '이용' 표현을 쓰는 유형 (주간보호·방문 계열) */
const USAGE_BASED_TYPES = new Set<string>([
  'DAY_CARE', 'HOME_VISIT_CARE', 'HOME_VISIT_BATH', 'HOME_VISIT_NURSING', 'HOME_CARE_CENTER',
])

/**
 * 시설 데이터 기반 FAQ 생성 — AI 검색(GEO)·SEO 용.
 * 규칙 기반(LLM 없음) — 모든 문장은 필드 값에서만 조립하고, 데이터 없는 질문은 생략한다.
 * 하위권(percentile>50)·무점수 시설에는 서열 표현을 쓰지 않는다(isTopHalfPercentile — buildFacilityAnalysis와 동일 정책).
 */
function buildFacilityFaqs(f: FacilityDetail): { question: string; answer: string }[] {
  const faqs: { question: string; answer: string }[] = []
  const rc = f.regionComparison

  // 1) 비용 — 비급여 월 합계(비용 탭과 동일하게 일액 ×30) + 지역 평균 대비 + 비용계산기 안내(고정 문장)
  if (f.nonCoveredCosts && f.nonCoveredCosts.length > 0) {
    const monthlyTotal = f.nonCoveredCosts.reduce((sum, c) => sum + c.amount * 30, 0)
    const parts = [
      `공공데이터(NHIS)에 등록된 비급여 항목은 ${f.nonCoveredCosts.length}개이며, 월 합계는 약 ${formatKrwCompact(monthlyTotal)}입니다.`,
    ]
    if (rc && rc.avgMonthlyNonCovered != null && rc.diffPercent != null) {
      // avgMonthlyNonCovered는 백엔드에서 ×30 없이 내려오는 일액 합계 평균 — 월 합계와 같은 단위로 맞추기 위해 ×30 환산.
      // diffPercent는 원시/원시 비율이라 환산 없이 그대로 사용한다.
      const avg = formatKrwCompact(rc.avgMonthlyNonCovered * 30)
      parts.push(
        rc.diffPercent === 0
          ? `지역 평균(${avg})과 비슷한 수준입니다.`
          : `지역 평균(${avg})보다 ${Math.abs(rc.diffPercent)}% ${rc.diffPercent < 0 ? '낮은' : '높은'} 수준입니다.`,
      )
    }
    parts.push('장기요양등급별 본인부담금은 별도이며, 비용계산기에서 등급·감경 조건을 넣으면 총 예상 비용을 확인할 수 있습니다.')
    faqs.push({ question: `'${f.name}'의 비용은 얼마인가요?`, answer: parts.join(' ') })
  }

  // 2) 입소(이용) 가능 여부 — 정원·현원·여유·대기
  if (typeof f.capacity === 'number' && f.capacity > 0 && typeof f.currentCount === 'number' && f.currentCount >= 0) {
    const usageBased = USAGE_BASED_TYPES.has(f.facilityType as string)
    const occ = Math.round((f.currentCount / f.capacity) * 100)
    const vacancy = f.capacity - f.currentCount
    const parts = [
      `정원 ${f.capacity}명 중 현재 ${f.currentCount}명이 ${usageBased ? '이용하고 있어' : '입소해 있어'} 가동률은 약 ${occ}%입니다.`,
    ]
    if (vacancy > 0) parts.push(`현재 기준 여유 인원은 ${vacancy}명입니다.`)
    if (typeof f.waitingCount === 'number' && f.waitingCount > 0) parts.push(`대기 인원은 ${f.waitingCount}명입니다.`)
    parts.push('정확한 입소 가능 시점은 시설에 문의가 필요합니다.')
    faqs.push({
      question: usageBased ? '지금 이용할 수 있나요?' : '지금 입소할 수 있나요?',
      answer: parts.join(' '),
    })
  }

  // 3) 평가등급 — 상위 50% 이내일 때만 백분위 표기(하위권 서열 표현 금지)
  if (f.evaluationGrade) {
    const parts = [
      `이 시설은 ${buildEvaluationSourcePhrase(f.evaluationCategory, f.evaluationServiceKind, f.facilityType)}에서 ${f.evaluationGrade}등급${f.evaluationScore != null ? `(${f.evaluationScore}점)` : ''}을 받았습니다.`,
    ]
    if (rc && rc.typeLabel && rc.totalInRegion != null && isTopHalfPercentile(rc.percentile)) {
      parts.push(`지역 내 ${rc.typeLabel} ${rc.totalInRegion}곳 중 평가 상위 ${rc.percentile}%입니다.`)
    }
    // 2025년 정기평가부터 5영역 → 4영역 개편. 신 4영역 점수가 있으면 4영역 문구를 쓴다
    // (evaluationScoreAreas와 동일한 판정 기준 — FAQPage JSON-LD 로도 나가므로 사실관계 일치 필요)
    const isV2Evaluation =
      f.scoreV2Operation != null || f.scoreV2Respect != null || f.scoreV2Service != null || f.scoreV2Result != null
    parts.push(
      isV2Evaluation
        ? '평가등급은 A(최우수)부터 E까지, 기관운영·수급자존중·서비스제공·서비스결과 4개 영역을 종합해 산정됩니다.'
        : '평가등급은 A(최우수)부터 E까지, 기관 운영·환경 및 안전·수급자 권리·급여 제공과정·급여 제공결과 5개 영역을 종합해 산정됩니다.',
    )
    parts.push('평가는 급여종류별로 산정되며, 같은 기관이라도 급여종류에 따라 등급이 다를 수 있습니다.')
    faqs.push({ question: '평가등급은 어떤가요?', answer: parts.join(' ') })
  }

  // 4) 인력 구성 — StaffInfoCard와 동일한 파싱(parseStaffMap) 재사용, 인원 많은 순 2~4개 직종
  const staff = parseStaffMap(f.staffInfo)
  if (staff) {
    const entries = Object.entries(staff).filter(([k, v]) => typeof v === 'number' && v > 0 && k !== '의사(총)')
    const top = entries.sort((a, b) => b[1] - a[1]).slice(0, 4)
    if (top.length >= 2) {
      faqs.push({
        question: '인력은 어떻게 구성되어 있나요?',
        answer: `주요 인력은 ${top.map(([name, count]) => `${name} ${count}명`).join(', ')}${entries.length > top.length ? ' 등' : ''}으로 구성되어 있습니다.`,
      })
    }
  }

  // 5) 프로그램 — 개수 + 유형 나열 + 치매 전담
  if (f.programs && f.programs.length > 0) {
    const typeNames = Array.from(new Set(f.programs.map((p) => p.programTypeName).filter(Boolean)))
    let answer = typeNames.length > 0
      ? `${typeNames.join('·')} 분야의 프로그램 ${f.programs.length}개를 운영하고 있습니다.`
      : `프로그램 ${f.programs.length}개를 운영하고 있습니다.`
    if (f.hasDementiaCare) answer += ' 치매 전담 케어가 가능합니다.'
    faqs.push({ question: '어떤 프로그램을 운영하나요?', answer })
  }

  return faqs
}

/**
 * 자주 묻는 질문(FAQ) 섹션 — 시설 데이터 기반 Q&A (SEO/GEO).
 * - 답변 텍스트는 details/summary 로 접힘 여부와 무관하게 항상 SSR HTML DOM에 존재한다.
 * - FAQPage JSON-LD의 질문/답변은 화면 표시 텍스트와 정확히 동일하다 (구글 정책).
 *   시설 상세에는 다른 FAQPage가 없어 페이지당 1개 유지.
 * - 생성된 질문이 2개 미만이면 섹션·JSON-LD 모두 미출력.
 */
function FacilityFAQSection({ facility }: { facility: FacilityDetail }) {
  const faqs = buildFacilityFaqs(facility)
  if (faqs.length < 2) return null

  return (
    <section className="mt-10" aria-label="자주 묻는 질문">
      {/* 비활성(noindex) 시설은 FAQPage JSON-LD 미출력 (structured-data-spec §5.1) — 화면 FAQ 는 유지 */}
      {facility.isActive !== false && <FAQPageJsonLd faqs={faqs} />}
      <h2 className="mb-4 text-lg font-bold text-slate-900">자주 묻는 질문</h2>
      <div className="flex flex-col gap-3">
        {faqs.map((faq) => (
          <details key={faq.question} className="rounded-2xl border border-slate-200 bg-white px-5 py-4">
            <summary className="cursor-pointer text-sm font-bold text-slate-900">
              {faq.question}
            </summary>
            <p className="mt-3 text-sm leading-relaxed text-slate-600">{faq.answer}</p>
          </details>
        ))}
      </div>
    </section>
  )
}

/** API 영문 키 → 한글 변환 */
const STAFF_LABEL: Record<string, string> = {
  drTotCnt: '의사(총)',
  mdeptSdrCnt: '의과 전문의',
  mdeptGdrCnt: '의과 일반의',
  mdeptIntnCnt: '인턴',
  mdeptResdntCnt: '레지던트',
  cmdcSdrCnt: '한방 전문의',
  cmdcGdrCnt: '한방 일반의',
  cmdcIntnCnt: '한방 인턴',
  cmdcResdntCnt: '한방 레지던트',
  detySdrCnt: '치과 전문의',
  detyGdrCnt: '치과 일반의',
  detyIntnCnt: '치과 인턴',
  detyResdntCnt: '치과 레지던트',
  pnursCnt: '조산사',
}

/**
 * staffInfo(JSON) 파싱 — 영문(camelCase) 키는 한글 라벨로 변환, 한국어 키('요양보호사1급' 등)는
 * 그대로 유지. 파싱 실패/비객체면 null. (StaffInfoCard·buildFacilityFaqs 공용)
 */
function parseStaffMap(staffInfo: string | null): Record<string, number> | null {
  if (!staffInfo) return null
  try {
    const parsed = typeof staffInfo === 'string' ? JSON.parse(staffInfo) : staffInfo
    if (typeof parsed !== 'object' || parsed === null) return null
    // 영문 키를 한글로 변환
    const staff: Record<string, number> = {}
    for (const [key, val] of Object.entries(parsed as Record<string, number>)) {
      const label = STAFF_LABEL[key] || key
      staff[label] = val
    }
    return staff
  } catch {
    return null
  }
}

/** 인력현황 카드 (요양보호사 비율 배지 포함) */
function StaffInfoCard({ staffInfo, capacity }: { staffInfo: string; capacity: number | null }) {
  const staff = parseStaffMap(staffInfo)
  if (!staff) return null

  const careWorkers = staff['요양보호사'] ?? staff['careWorker'] ?? 0
  const ratio = capacity && capacity > 0 ? careWorkers / capacity : null

  // 비율 평가
  let ratingLabel = ''
  let ratingClass = ''
  if (ratio !== null && careWorkers > 0) {
    if (ratio >= 0.33) {
      ratingLabel = '우수'
      ratingClass = 'bg-green-100 text-green-700 border-green-200'
    } else if (ratio >= 0.25) {
      ratingLabel = '적정'
      ratingClass = 'bg-blue-100 text-blue-700 border-blue-200'
    } else {
      ratingLabel = '보통'
      ratingClass = 'bg-cb-gray-100 text-cb-gray-600 border-cb-gray-200'
    }
  }

  // 표시할 인력 항목 (0명 제외, 의사총수는 별도 표시)
  const staffEntries = Object.entries(staff).filter(([k, v]) => v > 0 && k !== '의사(총)')

  if (staffEntries.length === 0) return null

  return (
    <Card>
      <h4 className="text-body font-bold text-cb-gray-900 mb-4 flex items-center gap-2">
        <Shield className="h-4.5 w-4.5 text-cb-secondary" size={18} />
        인력 현황
      </h4>

      {/* 요양보호사 비율 하이라이트 */}
      {careWorkers > 0 && (
        <div className="mb-4 rounded-xl bg-cb-gray-50 p-3.5">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-body-sm font-bold text-cb-gray-900">
                요양보호사 {careWorkers}명
              </p>
              {ratio !== null && capacity && capacity > 0 && (
                <p className="text-caption text-cb-gray-500 mt-0.5">
                  정원 {capacity}명 대비 1:{Math.round(1 / ratio)}
                </p>
              )}
            </div>
            {ratingLabel && (
              <span className={`rounded-lg border px-3 py-1 text-body-sm font-bold ${ratingClass}`}>
                {ratingLabel}
              </span>
            )}
          </div>
          {ratio !== null && (
            <div className="mt-2.5 h-2 w-full rounded-full bg-cb-gray-200 overflow-hidden">
              <div
                className={`h-full rounded-full transition-all ${
                  ratio >= 0.33 ? 'bg-green-500' : ratio >= 0.25 ? 'bg-blue-500' : 'bg-cb-gray-400'
                }`}
                style={{ width: `${Math.min(ratio * 200, 100)}%` }}
              />
            </div>
          )}
        </div>
      )}

      {/* 전체 인력 구성 */}
      <div className="flex flex-wrap gap-2">
        {staffEntries.map(([name, count]) => (
          <span
            key={name}
            className={`rounded-lg px-2.5 py-1 text-caption ${
              name === '요양보호사'
                ? 'bg-cb-secondary-light text-cb-secondary font-bold'
                : 'bg-cb-gray-50 text-cb-gray-600'
            }`}
          >
            {name} {count}명
          </span>
        ))}
      </div>
    </Card>
  )
}

function toPhotoUrl(url: string) {
  if (url.startsWith('http')) return url
  return `${API_BASE_URL}/files/${url}`
}

/** 사진 그리드 갤러리 (탭 내부용) */
function PhotoGridGallery({ photos, facilityName }: { photos: { id: number; url: string; caption?: string | null }[]; facilityName: string }) {
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null)

  return (
    <>
      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
        {photos.map((photo, i) => (
          <button
            key={photo.id}
            onClick={() => setLightboxIndex(i)}
            className="relative aspect-[4/3] rounded-xl overflow-hidden bg-gray-100 group"
          >
            <img
              src={toPhotoUrl(photo.url)}
              alt={photo.caption ?? `${facilityName} 사진 ${i + 1}`}
              className="h-full w-full object-cover group-hover:scale-105 transition-transform duration-300"
              loading="lazy"
            />
            {photo.caption && (
              <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/60 to-transparent p-2 pt-6">
                <p className="text-[11px] text-white truncate">{photo.caption}</p>
              </div>
            )}
          </button>
        ))}
      </div>

      {/* 라이트박스 */}
      <AnimatePresence>
        {lightboxIndex !== null && (
          <motion.div
            className="fixed inset-0 z-[100] bg-black/90 flex items-center justify-center"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setLightboxIndex(null)}
          >
            <button
              onClick={() => setLightboxIndex(null)}
              className="absolute top-4 right-4 flex h-10 w-10 items-center justify-center rounded-full bg-white/10 text-white hover:bg-white/20 z-10"
            >
              <X size={24} />
            </button>
            <img
              src={toPhotoUrl(photos[lightboxIndex]!.url)}
              alt={photos[lightboxIndex]!.caption ?? facilityName}
              className="max-h-[85vh] max-w-[90vw] object-contain"
              onClick={(e) => e.stopPropagation()}
            />
            {photos.length > 1 && (
              <>
                <button
                  onClick={(e) => { e.stopPropagation(); setLightboxIndex((lightboxIndex - 1 + photos.length) % photos.length) }}
                  className="absolute left-4 top-1/2 -translate-y-1/2 flex h-12 w-12 items-center justify-center rounded-full bg-white/10 text-white hover:bg-white/20"
                >
                  <ChevronLeft size={24} />
                </button>
                <button
                  onClick={(e) => { e.stopPropagation(); setLightboxIndex((lightboxIndex + 1) % photos.length) }}
                  className="absolute right-4 top-1/2 -translate-y-1/2 flex h-12 w-12 items-center justify-center rounded-full bg-white/10 text-white hover:bg-white/20"
                >
                  <ChevronRight size={24} />
                </button>
              </>
            )}
            {photos[lightboxIndex]!.caption && (
              <p className="absolute bottom-6 text-sm text-white/80">{photos[lightboxIndex]!.caption}</p>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </>
  )
}

/** 시설 히어로 이미지 (이미지 없으면 렌더링 안 함) */
function FacilityHeroImage({ url, name }: { url?: string | null; name: string }) {
  const [imgError, setImgError] = useState(false)

  if (!url || imgError) return null

  return (
    <div className="relative h-48 w-full bg-cb-gray-100">
      <img
        src={url}
        alt={`${name} 시설 사진`}
        onError={() => setImgError(true)}
        className="h-full w-full object-cover"
      />
      <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent" />
    </div>
  )
}

/** 시설 정보 행 (아이콘 + 라벨 + 값) */
function InfoRow({
  icon,
  label,
  value,
  valueClass,
}: {
  icon: React.ReactNode
  label: string
  value: string
  valueClass?: string
}) {
  return (
    <div className="flex items-center gap-3 text-body-sm rounded-xl bg-cb-gray-50 px-3.5 py-2.5">
      <span className="flex-shrink-0">{icon}</span>
      <span className="text-cb-gray-500 w-20 flex-shrink-0">{label}</span>
      <span className={valueClass ?? 'text-cb-gray-900 font-medium'}>{value}</span>
    </div>
  )
}

/**
 * 다중 라인 텍스트 행 — 교통편처럼 길고 줄바꿈이 있는 값에 사용.
 * NHIS 응답은 보통 한 줄에 항목들이 모두 붙어 있어(예: "지하철: 6호선...3호선...자가용:...")
 * 알려진 교통수단 키워드 앞에 줄바꿈을 강제 삽입해 가독성을 높인다.
 */
function InfoBlock({
  icon,
  label,
  value,
}: {
  icon: React.ReactNode
  label: string
  value: string
}) {
  const cleaned = value
    .replace(/[ \t]+/g, ' ')
    // 한국 NHIS 텍스트에 자주 등장하는 글머리(·ㆍ•) 를 표준 글머리로 통일하고 줄바꿈
    .replace(/[·ㆍ•]\s*/g, '\n• ')
    // 교통수단 키워드 + 콜론 앞에 줄바꿈 (단어 경계: 줄 시작/공백/문장부호 다음에만)
    .replace(
      /([\s.,!?)\]]|^)(주소|지하철|지하|버스|시내버스|시외버스|광역버스|마을버스|간선버스|지선버스|자가용|차량|승용차|KTX|기차|철도|도보|자전거|택시)\s*[:：]/g,
      '$1\n$2:',
    )
    .replace(/^\s*\n+/, '')
    .trim()
  return (
    <div className="rounded-xl bg-cb-gray-50 px-3.5 py-2.5 text-body-sm">
      <div className="mb-1.5 flex items-center gap-2">
        <span className="flex-shrink-0">{icon}</span>
        <span className="text-cb-gray-500">{label}</span>
      </div>
      <p className="whitespace-pre-line text-cb-gray-900 leading-relaxed">{cleaned}</p>
    </div>
  )
}

/**
 * 시설현황 카드 — `room_info` JSON ({"1인실": 0, "4인실": 8, ...}) 을 그리드로 표시.
 * 영구지침: docs/develop/facility/data-sync/detail-domains.md §3.3.
 * 값이 0 이거나 키가 없으면 해당 항목 숨김 (잡 C 가 0 응답 시 키 자체를 제외하지만 안전망으로).
 */
function RoomInfoCard({ roomInfo }: { roomInfo: string }) {
  let parsed: Record<string, number> | null = null
  try {
    const obj = JSON.parse(roomInfo)
    if (obj && typeof obj === 'object') parsed = obj as Record<string, number>
  } catch {
    return null
  }
  if (!parsed) return null
  const entries = Object.entries(parsed).filter(([, v]) => Number(v) > 0)
  if (entries.length === 0) return null

  return (
    <Card>
      <h4 className="text-body font-bold text-cb-gray-900 mb-4 flex items-center gap-2">
        <Building2 className="h-4.5 w-4.5 text-cb-primary" size={18} />
        시설 현황
      </h4>
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
        {entries.map(([label, value]) => (
          <div
            key={label}
            className="flex items-center justify-between rounded-xl bg-cb-gray-50 px-3 py-2.5"
          >
            <span className="text-caption text-cb-gray-500 truncate">{label}</span>
            <span className="ml-2 flex-shrink-0 text-body-sm font-bold text-cb-gray-900">
              {Number(value)}
            </span>
          </div>
        ))}
      </div>
    </Card>
  )
}

/** 요양병원 진료시간표 요일 정의 (월~일 순서) */
const HOSPITAL_DAYS: { key: 'mon' | 'tue' | 'wed' | 'thu' | 'fri' | 'sat' | 'sun'; label: string }[] = [
  { key: 'mon', label: '월' },
  { key: 'tue', label: '화' },
  { key: 'wed', label: '수' },
  { key: 'thu', label: '목' },
  { key: 'fri', label: '금' },
  { key: 'sat', label: '토' },
  { key: 'sun', label: '일' },
]

/** 진료시간 슬롯 → "09:00 ~ 17:00" 문자열. start/end 하나라도 없으면 null(휴진 처리). */
function formatTreatmentSlot(slot: TreatmentTimeSlot | null | undefined): string | null {
  if (!slot || !slot.start || !slot.end) return null
  return `${slot.start} ~ ${slot.end}`
}

/**
 * 요양병원 진료 정보 섹션 (FE-BE 계약 — hospitalDetail).
 *
 * - 진료시간 표 / 진료과목 / 병상 / 응급·주차 4개 하위 블록을 각각 카드로 렌더.
 * - 각 블록은 데이터가 있을 때만 노출하고, 전부 없으면 섹션 자체를 미출력(null 반환).
 * - 전 필드 null-safe. 렌더 경로에 window/Date/Math.random 미사용(SSR 하이드레이션 안전).
 * - 요양병원 유형에서만 호출된다(호출부에서 facilityType === 'NURSING_HOSPITAL' 가드).
 */
function HospitalInfoSection({ detail }: { detail: HospitalDetail }) {
  const th = detail.treatmentHours

  // 진료시간: 요일 중 하나라도 유효 슬롯이 있거나, 점심/접수/휴진 안내가 있으면 노출
  const dayRows = HOSPITAL_DAYS.map((d) => ({ label: d.label, hours: formatTreatmentSlot(th?.[d.key]) }))
  const hasAnyDay = dayRows.some((r) => r.hours !== null)
  const hasHourNotes = !!(th && (th.lunch || th.reception || th.noTreatmentSun || th.noTreatmentHoli))
  const showHours = hasAnyDay || hasHourNotes

  // 진료과목 (이름 없는 항목 방어적으로 제외)
  const departments = (detail.departments ?? []).filter((dp) => dp && dp.name)
  const showDepartments = departments.length > 0

  // 병상 (0 이하/미보유 항목 제외)
  const beds = detail.beds
  const bedItems = beds
    ? [
        { label: '허가', value: beds.permitted },
        { label: '일반', value: beds.standard },
        { label: '상급', value: beds.superior },
      ].filter((b) => typeof b.value === 'number' && b.value > 0)
    : []
  const showBeds = bedItems.length > 0

  // 응급 / 주차 / 위치 안내
  const hasEmergency = detail.emergencyDay === true || detail.emergencyNight === true
  const hasParking = typeof detail.parkingCapacity === 'number' || typeof detail.parkingFree === 'boolean'
  const hasLocation = !!(detail.locationGuide && detail.locationGuide.trim() !== '')
  const parkingText = [
    typeof detail.parkingCapacity === 'number' ? `${detail.parkingCapacity}대 가능` : null,
    typeof detail.parkingFree === 'boolean' ? (detail.parkingFree ? '무료' : '유료') : null,
  ].filter(Boolean).join(' · ')
  const showEmergencyParking = hasEmergency || hasParking || hasLocation

  // 전부 없으면 섹션 자체 미노출
  if (!showHours && !showDepartments && !showBeds && !showEmergencyParking) return null

  return (
    <>
      {/* 진료 시간 */}
      {showHours && (
        <Card>
          <h4 className="text-body font-bold text-cb-gray-900 mb-4 flex items-center gap-2">
            <Clock className="h-4.5 w-4.5 text-cb-primary" size={18} />
            진료 시간
          </h4>
          {hasAnyDay && (
            <div className="flex flex-col gap-1.5">
              {dayRows.map((row) => (
                <div
                  key={row.label}
                  className="flex items-center gap-3 rounded-xl bg-cb-gray-50 px-3.5 py-2"
                >
                  <span className="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-md bg-white text-caption font-bold text-cb-gray-600">
                    {row.label}
                  </span>
                  <span className={`text-body-sm font-medium ${row.hours ? 'text-cb-gray-900' : 'text-cb-gray-400'}`}>
                    {row.hours ?? '휴진'}
                  </span>
                </div>
              ))}
            </div>
          )}
          {hasHourNotes && (
            <div className={`flex flex-col gap-1 text-caption text-cb-gray-500 ${hasAnyDay ? 'mt-3' : ''}`}>
              {th?.lunch && <p>점심시간 {th.lunch}</p>}
              {th?.reception && <p>접수시간 {th.reception}</p>}
              {th?.noTreatmentSun && <p>일요일 {th.noTreatmentSun}</p>}
              {th?.noTreatmentHoli && <p>공휴일 {th.noTreatmentHoli}</p>}
            </div>
          )}
        </Card>
      )}

      {/* 진료과목 */}
      {showDepartments && (
        <Card>
          <h4 className="text-body font-bold text-cb-gray-900 mb-4 flex items-center gap-2">
            <Stethoscope className="h-4.5 w-4.5 text-cb-secondary" size={18} />
            진료과목 ({departments.length}개)
          </h4>
          <div className="flex flex-wrap gap-2">
            {departments.map((dp, idx) => (
              <span
                key={`${dp.name}-${idx}`}
                className="inline-flex items-center gap-1.5 rounded-lg bg-cb-gray-50 px-3 py-1.5 text-body-sm"
              >
                <span className="font-medium text-cb-gray-900">{dp.name}</span>
                {typeof dp.specialistCount === 'number' && dp.specialistCount > 0 && (
                  <span className="text-caption text-cb-gray-400">전문의 {dp.specialistCount}명</span>
                )}
              </span>
            ))}
          </div>
        </Card>
      )}

      {/* 병상 현황 */}
      {showBeds && (
        <Card>
          <h4 className="text-body font-bold text-cb-gray-900 mb-4 flex items-center gap-2">
            <BedDouble className="h-4.5 w-4.5 text-cb-primary" size={18} />
            병상 현황
          </h4>
          <div className="grid grid-cols-3 gap-2">
            {bedItems.map((b) => (
              <div key={b.label} className="flex flex-col items-center rounded-xl bg-cb-gray-50 px-3 py-3">
                <span className="text-caption text-cb-gray-500">{b.label} 병상</span>
                <span className="mt-1 text-h3 font-bold text-cb-gray-900">{b.value}</span>
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* 응급 · 주차 · 위치 안내 */}
      {showEmergencyParking && (
        <Card>
          <h4 className="text-body font-bold text-cb-gray-900 mb-4 flex items-center gap-2">
            <Siren className="h-4.5 w-4.5 text-cb-accent" size={18} />
            응급 · 주차 안내
          </h4>
          <div className="flex flex-col gap-3.5">
            {hasEmergency && (
              <div className="flex items-center gap-3 text-body-sm rounded-xl bg-cb-gray-50 px-3.5 py-2.5">
                <Siren size={16} className="flex-shrink-0 text-cb-accent" />
                <span className="text-cb-gray-500 w-20 flex-shrink-0">응급실</span>
                <span className="flex flex-wrap gap-1.5">
                  {detail.emergencyDay === true && (
                    <span className="rounded-md bg-cb-accent/10 px-2 py-0.5 text-caption font-bold text-cb-accent">주간 운영</span>
                  )}
                  {detail.emergencyNight === true && (
                    <span className="rounded-md bg-cb-accent/10 px-2 py-0.5 text-caption font-bold text-cb-accent">야간 운영</span>
                  )}
                </span>
              </div>
            )}
            {hasParking && parkingText !== '' && (
              <InfoRow
                icon={<Car size={16} className="text-cb-gray-400" />}
                label="주차"
                value={parkingText}
              />
            )}
            {hasLocation && (
              <div className="rounded-xl bg-cb-gray-50 px-3.5 py-2.5 text-body-sm">
                <div className="mb-1.5 flex items-center gap-2">
                  <MapPin size={16} className="flex-shrink-0 text-cb-gray-400" />
                  <span className="text-cb-gray-500">위치 안내</span>
                </div>
                <p className="text-cb-gray-900 leading-relaxed">{detail.locationGuide}</p>
              </div>
            )}
          </div>
        </Card>
      )}
    </>
  )
}

/**
 * 홈페이지 행 — 안전 URL 검증 후 새 탭 링크.
 * NHIS 응답은 protocol 이 빠진 형태(`www.example.com`) 가 흔하므로 `https://` 를 prefix.
 * AGENTS.md §보안 — `javascript:`/기타 스킴 차단.
 */
function HomepageRow({ url }: { url: string }) {
  const safeUrl = (() => {
    const trimmed = url.trim()
    if (/^https?:\/\//i.test(trimmed)) return trimmed
    if (/^[\w.-]+\.[a-z]{2,}/i.test(trimmed)) return `https://${trimmed}`
    return null
  })()
  return (
    <div className="flex items-center gap-3 text-body-sm rounded-xl bg-cb-gray-50 px-3.5 py-2.5">
      <Globe size={16} className="flex-shrink-0 text-cb-primary" />
      <span className="text-cb-gray-500 w-20 flex-shrink-0">홈페이지</span>
      {safeUrl ? (
        <a
          href={safeUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="truncate font-medium text-cb-primary hover:underline"
        >
          {url}
        </a>
      ) : (
        <span className="truncate text-cb-gray-900 font-medium">{url}</span>
      )}
    </div>
  )
}

/**
 * 같은 지역 시설 + 지역 랜딩 내부링크 섹션 (SEO 크롤 경로).
 *
 * - 지역 칩: 시도 / 시군구 / 시군구+유형 랜딩 페이지로 상향 링크
 * - 시설 카드: 같은 지역 동일 유형 시설 상세로 가는 링크 (현재 시설 제외, 최대 6건)
 * - 데이터는 entry-server.tsx 에서 SSR 프리페치(queryKey ['nearby-facilities', id])
 */
function RelatedFacilitiesSection({ facility }: { facility: FacilityDetail }) {
  const sido = facility.addressSido
  const sigungu = facility.addressSigungu
  const typeSlug = getFacilityTypeUrlSlug(facility.facilityType as string)
  const typeLabel = FACILITY_TYPE_LABELS[facility.facilityType as string] || '요양시설'
  const { data: nearby } = useNearbyFacilities(
    facility.id, sido, sigungu, facility.facilityType as string,
  )

  if (!sido || !sigungu) return null

  const regionUrl = `/facilities/${sido}/${sigungu}`
  const typeUrl = typeSlug ? `/facilities/${sido}/${sigungu}/${typeSlug}` : regionUrl
  const list = (nearby ?? []).filter((f) => f.id !== facility.id).slice(0, 6)

  const chipCls = 'inline-flex items-center rounded-full border border-slate-200 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 hover:border-cb-primary hover:text-cb-primary transition-colors'

  return (
    <section className="mt-10" aria-label="같은 지역 시설">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <h2 className="text-lg font-bold text-slate-900">{sigungu} {typeLabel} 더 보기</h2>
        <Link to={typeUrl} className="text-sm font-semibold text-cb-primary hover:underline">
          {sido} {sigungu} {typeLabel} 전체 보기 →
        </Link>
      </div>

      {/* 지역 랜딩 내부링크 칩 */}
      <div className="flex flex-wrap gap-2 mb-4">
        <Link to={`/facilities/${sido}`} className={chipCls}>{sido} 요양시설</Link>
        <Link to={regionUrl} className={chipCls}>{sido} {sigungu}</Link>
        {typeSlug && <Link to={typeUrl} className={chipCls}>{sigungu} {typeLabel}</Link>}
      </div>

      {/* 같은 지역 시설 카드 */}
      {list.length > 0 && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {list.map((f) => (
            <Link
              key={f.id}
              to={`/facilities/${f.id}`}
              className="block rounded-2xl border border-slate-200 bg-white p-4 hover:border-cb-primary hover:shadow-sm transition-all"
            >
              <p className="font-bold text-slate-900 truncate">{f.name}</p>
              <p className="mt-1 text-xs text-slate-500 truncate">
                {sigungu} · {FACILITY_TYPE_LABELS[f.facilityType as string] ?? f.facilityType}
              </p>
              {f.evaluationGrade && (
                <span className="mt-2 inline-block rounded-full bg-cb-primary/10 px-2 py-0.5 text-[11px] font-semibold text-cb-primary">
                  평가등급 {f.evaluationGrade}
                </span>
              )}
            </Link>
          ))}
        </div>
      )}
    </section>
  )
}

